import React from 'react';
import PropTypes from 'prop-types';
import {action} from 'mobx';
import {inject, observer} from 'mobx-react';
import {withRouter, routerShape} from 'react-router';

import autobind from 'autobind-decorator';
import {withConfig, withI18n} from 'spa/Components/Context';
import Alert from 'spa/Components/Alert';

import ApplePayOption from './ApplePayOption';
import CardOption from './CardOption';
import PaymentOptions from './PaymentOptions';
import PaypalOption from './PaypalOption';

import {PAYMENT_TYPES} from '../../state/payments/billing';

import {cn} from 'spa/css';
import '../CheckoutPaymentDetails/checkout-payment-details.less';

@withRouter
@inject(({
  store: {
    billing,
    session,
  },
}) =>
  ({
    billing,
    session,
  }))
@observer
@withConfig
@withI18n
class AddPaymentMethod extends React.Component {
  static propTypes = {
    billing: PropTypes.shape({
      abort: PropTypes.func.isRequired,
      canMakeApplePayments: PropTypes.bool,
      canUseApplePay: PropTypes.bool,
      canUsePaypal: PropTypes.bool,
      cards: PropTypes.array.isRequired,
      chargeCity: PropTypes.string.isRequired,
      chargeCountry: PropTypes.string.isRequired,
      error: PropTypes.object,
      initGateway: PropTypes.func.isRequired,
      isLoading: PropTypes.bool.isRequired,
      maybeLoad: PropTypes.func.isRequired,
      paypalGatewayReady: PropTypes.string,
      paymentInfo: PropTypes.shape({
        paymentOption: PropTypes.string,
        paymentToken: PropTypes.string,
      }).isRequired,
      paypalAccounts: PropTypes.array,
      setPaymentOption: PropTypes.func.isRequired,
      setPaymentToken: PropTypes.func.isRequired,
      updatePaymentMethodChanged: PropTypes.func.isRequired,
    }).isRequired,
    className: PropTypes.string,
    disabled: PropTypes.bool,
    narrowVersion: PropTypes.bool,
    onApplePay: PropTypes.func,
    processingForm: PropTypes.bool,
    router: routerShape.isRequired,
    session: PropTypes.shape({
      user: PropTypes.object,
    }).isRequired,
    trackingCategory: PropTypes.string,
  };

  static defaultProps = {
    className: undefined,
    disabled: false,
    narrowVersion: false,
    onApplePay: undefined,
    processingForm: false,
  };

  constructor(props, context) {
    super(props);

    // We need this here, since we want an up to date list when we show this component
    props.billing.maybeLoad();
  }

  componentWillUnmount() {
    const {billing} = this.props;

    billing.abort();
  }

  @autobind
  @action('onChangeOption - AddPaymentMethod')
  onChangeOption(event) {
    const {
      billing: {
        cards,
        chargeCountry,
        paypalAccounts,
        initGateway,
        setPaymentOption,
        setPaymentToken,
        updatePaymentMethodChanged,
      },
      session,
    } = this.props;

    setPaymentOption(event.target.value);

    let list;
    if (event.target.value === PAYMENT_TYPES.CARD) {
      list = cards;
    } else if (event.target.value === PAYMENT_TYPES.PAYPAL) {
      list = paypalAccounts;
    }
    if (list && list.length) {
      setPaymentToken(list[0].id);
    } else {
      setPaymentToken('');
    }

    if (list && !list.length && event.target.value === PAYMENT_TYPES.CARD) {
      this.newCardForm = true;
    } else {
      this.newCardForm = false;
    }

    updatePaymentMethodChanged(true);

    initGateway(chargeCountry, session.user.id, event.target.value);
  }

  @autobind
  @action('onChangeToken - AddPaymentMethod')
  onChangeToken(event) {
    const {
      billing: {
        setPaymentToken,
        updatePaymentMethodChanged,
      },
    } = this.props;

    setPaymentToken(event.target.value);
    updatePaymentMethodChanged(true);
  }

  render() {
    const {
      context,
      onChangeToken,
      onChangeOption,
      props,
    } = this;
    const {
      billing,
      className,
      disabled,
      narrowVersion,
      onApplePay,
      processingForm,
      trackingCategory,
    } = props;
    const {
      canMakeApplePayments,
      canUseApplePay,
      canUsePaypal,
      chargeCountry,
      paymentInfo,
      paypalAccounts,
      setPaymentToken,
    } = billing;
    const {i18n} = context;
    const {isLoading, error} = billing;

    return (
      <div
        className={cn(
          className,
          narrowVersion && 'checkout-payment-details-wrapper--narrow',
        )}
      >
        <input
          type="hidden"
          name="payment-type"
          value="charge"
        />
        {error && <Alert type="error" title={i18n.__error(error)} />}
        <div className="checkout-payment-details">
          <PaymentOptions
            canMakeApplePayments={canMakeApplePayments}
            canUseApplePay={canUseApplePay}
            canUsePaypal={canUsePaypal}
            disabled={disabled}
            onChangeOption={onChangeOption}
            paymentInfo={paymentInfo}
          />
          <div className="checkout-payment-details__info-container">
            {isLoading ? (
              <div className="checkout-payment-details__payment-methods">
                <div className="checkout-payment-details__payment-method checkout-payment-details__payment-method--loading placeholder-box" />
                <div className="checkout-payment-details__payment-method checkout-payment-details__payment-method--loading checkout-payment-details__payment-method--no-border-bottom placeholder-box" />
              </div>
            ) : (
              <React.Fragment>
                <React.Fragment>
                  {paymentInfo.paymentOption === PAYMENT_TYPES.CARD && (
                    <CardOption
                      disabled={disabled}
                      onChangeToken={onChangeToken}
                      trackingCategory={trackingCategory}
                    />
                  )}
                </React.Fragment>
                {billing.paypalGatewayReady && paymentInfo.paymentOption === PAYMENT_TYPES.PAYPAL && (
                  <PaypalOption
                    canUsePaypal={canUsePaypal}
                    chargeCountry={chargeCountry}
                    disabled={disabled}
                    onChangeToken={onChangeToken}
                    paypalAccounts={paypalAccounts}
                    paymentInfo={paymentInfo}
                    setPaymentToken={setPaymentToken}
                  />
                )}
                <div className="checkout-payment-details__button-wrapper">
                  <ApplePayOption
                    canUseApplePay={canUseApplePay}
                    i18n={i18n}
                    onApplePay={onApplePay}
                    paymentInfo={paymentInfo}
                    processingForm={processingForm}
                  />
                </div>
              </React.Fragment>
            )}
          </div>
        </div>
      </div>
    );
  }
}

export default AddPaymentMethod;



// WEBPACK FOOTER //
// ./spa/Components/AddPaymentMethod/AddPaymentMethod.jsx